import type { PersonId } from '@shared/brand';
import type {
  IAuditLogRepository,
  IAuthGateway,
  IBalanceRepository,
  IClock,
  IDocumentStorage,
  IEmergencyContactRepository,
  IDataConfirmationRepository,
  IPersonalRecordRepository,
  ISalaryRepository,
  IHolidayRepository,
  IIdGenerator,
  IIncapacityRepository,
  ILogger,
  IOrganizationRepository,
  IOrgChartRepository,
  IWhatsAppLinkRepository,
  IAssistantSettingsRepository,
  IBirthdayRepository,
  IPermissionRequestRepository,
  IPersonLeaderRepository,
  IPersonRepository,
  IRuntimeConfigRepository,
  IVacationRequestRepository,
} from '@application/ports';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import { announceBootstrapFlags, demoMode } from './bootstrapMode';
import { FirebaseAuthGateway } from './firebase/FirebaseAuthGateway';
import { initializeFirebase } from './firebase/client';
import { FirestoreRuntimeConfigRepository } from './repositories/FirestoreRuntimeConfigRepository';
import { FirestoreEmergencyContactRepository } from './repositories/FirestoreEmergencyContactRepository';
import { FirestoreDataConfirmationRepository } from './repositories/FirestoreDataConfirmationRepository';
import { FirestorePersonalRecordRepository } from './repositories/FirestorePersonalRecordRepository';
import { FirestoreSalaryRepository } from './repositories/FirestoreSalaryRepository';
import { FirestoreOrgChartRepository } from './repositories/FirestoreOrgChartRepository';
import { FirestoreWhatsAppLinkRepository } from './repositories/FirestoreWhatsAppLinkRepository';
import { FirestoreAssistantSettingsRepository } from './repositories/FirestoreAssistantSettingsRepository';
import { FirestoreBirthdayRepository } from './repositories/FirestoreBirthdayRepository';
import { FirestoreVacationRequestRepository } from './repositories/FirestoreVacationRequestRepository';
import {
  FirestoreAuditLogRepository,
  FirestoreIncapacityRepository,
  FirestorePermissionRequestRepository,
} from './repositories/FirestoreAbsenceRepositories';
import {
  FirestoreBalanceRepository,
  FirestoreHolidayRepository,
  FirestoreOrganizationRepository,
  FirestorePersonLeaderRepository,
  FirestorePersonRepository,
} from './repositories/FirestoreOrgRepositories';
import {
  DemoAuditLogRepository,
  DemoAuthGateway,
  DemoBalanceRepository,
  DemoEmergencyContactRepository,
  DemoDataConfirmationRepository,
  DemoOrgChartRepository,
  DemoWhatsAppLinkRepository,
  DemoAssistantSettingsRepository,
  DemoBirthdayRepository,
  DemoPersonalRecordRepository,
  DemoSalaryRepository,
  DemoHolidayRepository,
  DemoIncapacityRepository,
  DemoOrganizationRepository,
  DemoPermissionRequestRepository,
  DemoPersonLeaderRepository,
  DemoPersonRepository,
  DemoRuntimeConfigRepository,
  DemoVacationRequestRepository,
} from './demo/DemoRepositories';
import { DemoDocumentStorage, FirebaseDocumentStorage } from './storage/FirebaseDocumentStorage';
import { DEMO_TODAY, P_MARCO } from './demo/seed';

/**
 * The composition root.
 *
 * The single place where interfaces meet implementations. Everything upstream of
 * here — the domain, the use cases, the view-models — knows only the ports, which
 * is what lets the whole application layer be tested against in-memory fakes and
 * what makes a future PostgreSQL adapter a matter of writing new classes rather
 * than editing business logic.
 */

/** Wall-clock time. The only place `new Date()` is legitimate in app code. */
export class SystemClock implements IClock {
  now(): Date {
    return new Date();
  }

  /**
   * Today in the viewer's own timezone.
   *
   * The one place a timezone legitimately matters: "today" for someone in Mexico
   * City is a local question, and a UTC answer would be a day ahead for most of the
   * evening. Everything downstream is zone-free again.
   */
  today(): CalendarDate {
    return CalendarDate.fromInstantLocal(new Date());
  }
}

/** The handoff is anchored to 27 July 2026; demo mode must render that same day. */
class DemoClock implements IClock {
  now(): Date {
    return new Date('2026-07-27T09:00:00.000Z');
  }

  today(): CalendarDate {
    return DEMO_TODAY;
  }
}

export class CryptoIdGenerator implements IIdGenerator {
  generate(): string {
    return crypto.randomUUID();
  }
}

/**
 * Console logger.
 *
 * `debug` is silenced in production rather than shipped, because the debug lines
 * here carry document paths and person ids. Warnings and errors are kept: they are
 * how an undecodable document or a missing index becomes visible.
 */
export class ConsoleLogger implements ILogger {
  constructor(private readonly verbose: boolean) {}

  debug(message: string, context?: Readonly<Record<string, unknown>>): void {
    if (!this.verbose) return;
    console.warn(`[debug] ${message}`, context ?? {});
  }

  warn(message: string, context?: Readonly<Record<string, unknown>>): void {
    console.warn(`[warn] ${message}`, context ?? {});
  }

  error(message: string, context?: Readonly<Record<string, unknown>>): void {
    console.error(`[error] ${message}`, context ?? {});
  }
}

/**
 * The service graph, typed against the ports rather than the adapters.
 *
 * Typing these as the interfaces is what lets demo mode substitute the whole
 * persistence layer without a single change above this file.
 */
export interface Services {
  readonly clock: IClock;
  readonly ids: IIdGenerator;
  readonly logger: ILogger;
  readonly auth: IAuthGateway;
  readonly config: IRuntimeConfigRepository;
  readonly organization: IOrganizationRepository;
  readonly people: IPersonRepository;
  readonly leaders: IPersonLeaderRepository;
  readonly requests: IVacationRequestRepository;
  readonly permissions: IPermissionRequestRepository;
  readonly incapacities: IIncapacityRepository;
  readonly holidays: IHolidayRepository;
  readonly balances: IBalanceRepository;
  readonly audit: IAuditLogRepository;
  /** Uploads the signed registro and the IMSS certificate. */
  readonly documents: IDocumentStorage;
  /*
   * The port, not the class.
   *
   * This was the one service in the graph typed against a concrete
   * implementation — the demo one — which is precisely how the in-memory fake sat
   * in the production branch unnoticed: the type asked for the fake, so the
   * compiler was satisfied. Typed as the interface, the mistake is a build error.
   */
  readonly contacts: IEmergencyContactRepository;
  readonly salaries: ISalaryRepository;
  readonly personalRecords: IPersonalRecordRepository;
  /** Lo que cada persona confirmó —o corrigió— de su propia ficha al entrar. */
  readonly dataConfirmations: IDataConfirmationRepository;
  /** El organigrama de cada área. Ver `IOrgChartRepository`. */
  readonly orgCharts: IOrgChartRepository;
  /** Los cumpleaños de la empresa, para Kn·Blog. Ver `IBirthdayRepository`. */
  readonly birthdays: IBirthdayRepository;
  /** El vínculo propio con el asistente de WhatsApp. Ver `IWhatsAppLinkRepository`. */
  readonly whatsapp: IWhatsAppLinkRepository;
  /** Qué modelo de lenguaje usa el asistente y sus llaves. Ver `IAssistantSettingsRepository`. */
  readonly assistant: IAssistantSettingsRepository;
  /** True when running against in-memory data. Screens use it to explain gaps. */
  readonly isDemo: boolean;
}

let services: Services | null = null;

/**
 * Builds the service graph. Called once from `main.tsx`, before React renders, so
 * a misconfigured environment fails with a readable message rather than inside a
 * component tree.
 */
export const buildServices = (): Services => {
  if (services !== null) return services;

  announceBootstrapFlags();

  const logger = new ConsoleLogger(import.meta.env.DEV);
  const clock = demoMode() ? new DemoClock() : new SystemClock();
  const ids = new CryptoIdGenerator();

  if (demoMode()) {
    // Firebase is never initialised in demo mode, so a missing or placeholder
    // configuration is irrelevant and no network call is attempted.
    demoAuth = new DemoAuthGateway(P_MARCO);

    services = {
      clock,
      ids,
      logger,
      auth: demoAuth,
      config: new DemoRuntimeConfigRepository(),
      organization: new DemoOrganizationRepository(),
      people: new DemoPersonRepository(),
      leaders: new DemoPersonLeaderRepository(),
      requests: new DemoVacationRequestRepository(),
      permissions: new DemoPermissionRequestRepository(),
      incapacities: new DemoIncapacityRepository(),
      holidays: new DemoHolidayRepository(),
      balances: new DemoBalanceRepository(),
      audit: new DemoAuditLogRepository(),
      documents: new DemoDocumentStorage(),
      contacts: new DemoEmergencyContactRepository(),
      salaries: new DemoSalaryRepository(),
      personalRecords: new DemoPersonalRecordRepository(),
      dataConfirmations: new DemoDataConfirmationRepository(),
      orgCharts: new DemoOrgChartRepository(),
      birthdays: new DemoBirthdayRepository(),
      whatsapp: new DemoWhatsAppLinkRepository(),
      assistant: new DemoAssistantSettingsRepository(),
      isDemo: true,
    };

    return services;
  }

  initializeFirebase();

  services = {
    clock,
    ids,
    logger,
    auth: new FirebaseAuthGateway(),
    config: new FirestoreRuntimeConfigRepository(logger),
    organization: new FirestoreOrganizationRepository(logger),
    people: new FirestorePersonRepository(logger),
    leaders: new FirestorePersonLeaderRepository(logger),
    requests: new FirestoreVacationRequestRepository(logger),
    permissions: new FirestorePermissionRequestRepository(logger),
    incapacities: new FirestoreIncapacityRepository(logger),
    holidays: new FirestoreHolidayRepository(logger),
    balances: new FirestoreBalanceRepository(logger),
    audit: new FirestoreAuditLogRepository(logger),
    documents: new FirebaseDocumentStorage(),
    contacts: new FirestoreEmergencyContactRepository(logger),
    /*
     * Todavía sin adaptador de Firestore: la colección y sus reglas van en
     * `feat/backend-completo`, porque `firestore.rules` es artefacto de
     * producción y esta rama no despliega. Falla ruidosamente en vez de devolver
     * `null`, que se leería como «esta persona no tiene sueldo».
     */
    salaries: new FirestoreSalaryRepository(),
    personalRecords: new FirestorePersonalRecordRepository(),
    dataConfirmations: new FirestoreDataConfirmationRepository(),
    orgCharts: new FirestoreOrgChartRepository(),
    birthdays: new FirestoreBirthdayRepository(),
    whatsapp: new FirestoreWhatsAppLinkRepository(),
    assistant: new FirestoreAssistantSettingsRepository(),
    isDemo: false,
  };

  return services;
};

/**
 * Switches the acting person in demo mode.
 *
 * The four roles show four materially different applications, and without a backend
 * there is no other way to review them. Throws outside demo mode — impersonating a
 * colleague is exactly the thing the real auth path must never allow.
 */
export const demoActAs = (personId: PersonId): void => {
  if (demoAuth === null) {
    throw new Error('demoActAs sólo existe en modo demo.');
  }
  demoAuth.actAs(personId);
};

let demoAuth: DemoAuthGateway | null = null;

export const getServices = (): Services => {
  if (services === null) {
    throw new Error('buildServices() no se ha llamado. Debe ejecutarse en src/main.tsx.');
  }
  return services;
};
